/**
 * @file PtEvents instrumentation for first-party conversion and intent surfaces.
 */

import { logPtEvent } from '../utils/pt-events';

const EVENT_CATEGORY = 'site';
const EVENT_TAGS = ['frontend'];
const MAX_TEXT_LENGTH = 200;

const linkTrackers = [
	{
		selector: '.v-highlight-card__link',
		name: 'wordpress_highlight_card_cta_clicked',
		component: 'highlight-card',
		componentSelector: '[data-pt-component="highlight-card"]',
		titleSelector: '.v-highlight-card__title',
	},
	{
		selector: '.v-provider-card__link',
		name: 'wordpress_provider_card_cta_clicked',
		component: 'provider-card',
		componentSelector: '.v-provider-card',
		itemSelector: '.v-provider-card__item',
		titleSelector: '.v-provider-card__title-label',
	},
	{
		selector: '.v-resource-list__card-link',
		name: 'wordpress_resource_card_cta_clicked',
		component: 'resource-list',
		componentSelector: '.v-resource-list',
		itemSelector: '.v-resource-list__item',
		titleSelector: '.v-resource-list__card-title',
	},
	{
		selector: '.v-link-list__link',
		name: 'wordpress_link_list_link_clicked',
		component: 'link-list',
		componentSelector: '.v-link-list',
		itemSelector: '.v-link-list__item',
		titleSelector: '.v-link-list__title',
		titleScope: 'component',
	},
];

/**
 * Normalize text included with an analytics event.
 *
 * @param {string} value - Text to normalize.
 * @returns {string} Normalized, bounded text.
 */
const normalizeText = (value = '') =>
	value.replace(/\s+/g, ' ').trim().slice(0, MAX_TEXT_LENGTH);

/**
 * Get the one-based position of an element among matching elements.
 *
 * @param {Element} element - Element whose position is needed.
 * @param {string} selector - Selector for the element collection.
 * @param {Document|Element} scope - Collection scope.
 * @returns {number|null} One-based position, or null if not found.
 */
const getPosition = (element, selector, scope = document) => {
	const position = [...scope.querySelectorAll(selector)].indexOf(element);
	return position >= 0 ? position + 1 : null;
};

/**
 * Classify a link destination without relying on template-specific data.
 *
 * @param {string} href - Link destination.
 * @returns {string} Destination type.
 */
const getDestinationType = (href) => {
	if (href.startsWith('mailto:')) return 'email';
	if (href.startsWith('tel:')) return 'telephone';

	try {
		const destination = new URL(href, window.location.href);
		if (
			destination.origin === window.location.origin &&
			destination.pathname === window.location.pathname &&
			destination.search === window.location.search &&
			destination.hash
		) {
			return 'same-page';
		}

		return destination.origin === window.location.origin
			? 'internal'
			: 'external';
	} catch {
		return 'unknown';
	}
};

/**
 * Build shared page context.
 *
 * @returns {Object} Shared page context.
 */
const getPageContext = () => ({
	pagePath: window.location.pathname,
	pageTitle: document.title,
});

/**
 * Select the element that contains a tracker's title.
 *
 * @param {Object} tracker - Link tracker configuration.
 * @param {Element|null} component - Matched component element.
 * @param {Element|null} item - Matched item element.
 * @returns {Element|null} Element to query for the configured title.
 */
const getTitleScope = (tracker, component, item) =>
	tracker.titleScope === 'component' ? component : item || component;

/**
 * Log clicks on the configured first-party CTA and link surfaces.
 *
 * @param {MouseEvent} event - Document click event.
 */
const trackLinkClick = (event) => {
	if (!(event.target instanceof Element)) return;

	const tracker = linkTrackers.find(({ selector }) =>
		event.target.closest(selector),
	);

	if (!tracker) return;

	const link = event.target.closest(tracker.selector);
	const component = link.closest(tracker.componentSelector);
	const item = tracker.itemSelector ? link.closest(tracker.itemSelector) : null;
	const titleScope = getTitleScope(tracker, component, item);
	const title = titleScope?.querySelector(tracker.titleSelector);

	logPtEvent(EVENT_CATEGORY, tracker.name, EVENT_TAGS, {
		...getPageContext(),
		component: tracker.component,
		componentPosition: component
			? getPosition(component, tracker.componentSelector)
			: null,
		itemPosition:
			item && component
				? getPosition(item, tracker.itemSelector, component)
				: null,
		itemTitle: normalizeText(title?.textContent),
		linkLabel: normalizeText(link.textContent),
		destination: link.href,
		destinationType: getDestinationType(link.getAttribute('href') || ''),
	});
};

/**
 * Log site-search submissions without retaining the visitor's raw query.
 */
const trackSearchForms = () => {
	const searchInputs = document.querySelectorAll('.search-field[name="s"]');

	searchInputs.forEach((searchInput, index) => {
		const form = searchInput.closest('form');
		if (!form) return;

		form.addEventListener('submit', () => {
			const query = searchInput.value.trim();

			logPtEvent(
				EVENT_CATEGORY,
				'wordpress_site_search_submitted',
				EVENT_TAGS,
				{
					...getPageContext(),
					component: 'search-form',
					formPosition: index + 1,
					hasQuery: query.length > 0,
					queryLength: query.length,
					destination: form.action,
				},
			);
		});
	});
};

/**
 * Initialize first-party PtEvents instrumentation.
 */
const vSiteEventTracking = () => {
	document.addEventListener('click', trackLinkClick);
	trackSearchForms();
};

export default vSiteEventTracking;
export { getPosition, getTitleScope, linkTrackers };
